import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TownConfig } from '../types/config';
import defaultConfig from '../config/townConfig.json';
import { useDashboardConfig } from '../hooks/useDashboardConfig';

interface ConfigContextType {
  config: TownConfig;
  updateConfig: (newConfig: Partial<TownConfig>) => void;
  saveNewDashboard: (name: string, config: TownConfig) => string;
}

export const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export function ConfigProvider({ children }: { children: React.ReactNode }) {
  const { id } = useParams();
  const { getConfig, updateConfig: updateStoredConfig, saveConfig } = useDashboardConfig();
  const [config, setConfig] = useState<TownConfig>(defaultConfig as TownConfig);

  useEffect(() => {
    if (id) {
      const storedConfig = getConfig(id);
      if (storedConfig) {
        setConfig(storedConfig.config);
      }
    }
  }, [id, getConfig]);

  const updateConfig = useCallback((newConfig: Partial<TownConfig>) => {
    setConfig(current => {
      const updated = {
        ...current,
        ...newConfig,
        data: {
          ...current.data,
          ...(newConfig.data || {})
        }
      };
      
      if (id) {
        updateStoredConfig(id, updated);
      }
      return updated;
    });
  }, [id, updateStoredConfig]);

  const saveNewDashboard = useCallback((name: string, dashboardConfig: TownConfig) => {
    return saveConfig(name, dashboardConfig);
  }, [saveConfig]);

  return (
    <ConfigContext.Provider value={{ config, updateConfig, saveNewDashboard }}>
      {children}
    </ConfigContext.Provider>
  );
}

